import type { Band } from './types';

export interface DetectionConfig {
  airborneGsKt: number;      // 40
  airborneHafFt: number;     // 200
  openStreak: number;        // 3 consecutive airborne samples
  closeGroundS: number;      // 300 s continuously non-airborne
  closeGapS: number;         // 1200 s without data
  touchHafFt: number;        // 50
  touchCrossM: number;       // 60
  touchAlongPadM: number;    // 300
  touchTrackTolDeg: number;  // 20
  touchGapS: number;         // 10 s gap still same cluster
  stopGsKt: number;          // 40
  climbAwayHafFt: number;    // 200 within climbAwayS after event end
  climbAwayS: number;        // 60
  approachCrossM: number;    // 150 aligned-on-final lateral tolerance
  approachMaxNm: number;     // 5
  goAroundHafFt: number;     // 500
  goAroundVsFpm: number;     // 300
  lowApproachMinHafFt: number; // 20
  lowApproachMaxHafFt: number; // 200
  circuitMaxNm: number;      // 4
  circuitMaxHafFt: number;   // 2000
  legTolDeg: number;         // 30
  turnRateDps: number;       // 1.5 (standard rate is 3; threshold must sit well below it)
  finalCrossM: number;       // 300 lateral window to call a leg 'final'
  rcExcludeM: number;        // 185 exclude from parallel-runway assignment
  dtwDisagreeS: number;      // 20
  minPointsPerLeg: number;   // 3
  expectedDtS: number | null; // sample spacing the source can deliver (null = median of the flight, clamped 1–10 s); confidence density is relative to it
}

export const DEFAULT_DETECTION: DetectionConfig = {
  airborneGsKt: 40, airborneHafFt: 200, openStreak: 3, closeGroundS: 300, closeGapS: 1200,
  touchHafFt: 50, touchCrossM: 60, touchAlongPadM: 300, touchTrackTolDeg: 20, touchGapS: 10,
  stopGsKt: 40, climbAwayHafFt: 200, climbAwayS: 60,
  approachCrossM: 150, approachMaxNm: 5, goAroundHafFt: 500, goAroundVsFpm: 300,
  lowApproachMinHafFt: 20, lowApproachMaxHafFt: 200,
  circuitMaxNm: 4, circuitMaxHafFt: 2000, legTolDeg: 30, turnRateDps: 1.5, finalCrossM: 300,
  rcExcludeM: 185, dtwDisagreeS: 20, minPointsPerLeg: 3, expectedDtS: null,
};

export interface ScoringConfig {
  tpaFt: number;             // 1000
  downwindNm: number;        // 0.75
  turnRadiusNm: number;      // 0.25 (ideal pattern geometry)
  touchdownZoneFt: [number, number]; // [500, 1000]
  approachStartHafFt: number; // 500
  minConfidence: number;     // 0.5
  bands: {
    downwindDistanceNm: Band;   // raw = |mean cross - ideal|
    altitudeHoldFt: Band;       // raw = RMS deviation from TPA
    legSquarenessDeg: Band;     // raw = mean |deviation from perpendicular|
    finalOvershootM: Band;      // raw = max overshoot past centreline
    finalRolloutNm: Band;       // raw = distance from threshold at rollout (higher better)
    speedConsistencyKt: Band;   // raw = std dev
    glidepathDeg: Band;         // raw = mean approach angle to the aim point, deg
    descentRateShare: Band;     // raw = share of samples in 300..800 fpm (higher better)
    speedStabilityKt: Band;     // raw = std dev of airspeed proxy
    centrelineM: Band;          // raw = RMS cross
    touchdownFt: Band;          // raw = along at touch, ft
  };
  weights: { pattern: Record<string, number>; approach: Record<string, number> };
}

export const DEFAULT_SCORING: ScoringConfig = {
  tpaFt: 1000, downwindNm: 0.75, turnRadiusNm: 0.25, touchdownZoneFt: [500, 1000],
  approachStartHafFt: 500, minConfidence: 0.5,
  bands: {
    downwindDistanceNm: { green: [0, 0.15], red: [0, 0.6] },
    altitudeHoldFt: { green: [0, 75], red: [0, 300] },
    legSquarenessDeg: { green: [0, 15], red: [0, 45] },
    finalOvershootM: { green: [0, 60], red: [0, 300] },
    finalRolloutNm: { green: [0.4, 10], red: [0.1, 10] },
    speedConsistencyKt: { green: [0, 5], red: [0, 20] },
    glidepathDeg: { green: [2.5, 4.5], red: [1.5, 6.5] },
    descentRateShare: { green: [0.8, 1], red: [0.3, 1] },
    speedStabilityKt: { green: [0, 4], red: [0, 15] },
    centrelineM: { green: [0, 20], red: [0, 100] },
    touchdownFt: { green: [500, 1000], red: [200, 2000] },
  },
  weights: {
    pattern: { downwindDistance: 1, altitudeHold: 1, legSquareness: 1, finalOvershoot: 0.5, finalRollout: 0.5, speedConsistency: 0.5 },
    approach: { glidepath: 1, descentRate: 1, speedStability: 1, centreline: 1, touchdown: 1 },
  },
};

export function mergeScoring(partial: Partial<ScoringConfig>): ScoringConfig {
  return { ...DEFAULT_SCORING, ...partial, bands: { ...DEFAULT_SCORING.bands, ...(partial.bands ?? {}) },
    weights: { pattern: { ...DEFAULT_SCORING.weights.pattern, ...(partial.weights?.pattern ?? {}) },
               approach: { ...DEFAULT_SCORING.weights.approach, ...(partial.weights?.approach ?? {}) } } };
}

export function mergeDetection(partial: Partial<DetectionConfig>): DetectionConfig {
  return { ...DEFAULT_DETECTION, ...partial };
}
